// utils/addressUtils.js
const GOOGLE_MAPS_API_KEY = 'AIzaSyDG7_6lxN0J7dEDP1maUwBzoOlWd0wqpFQ';

export const loadGoogleMapsScript = () => {
  return new Promise((resolve, reject) => {
    // Check if already loaded
    if (window.google && window.google.maps && window.google.maps.places) {
      resolve();
      return;
    }

    // Check if script is already loading
    if (document.querySelector('script[src*="maps.googleapis.com"]')) {
      // Script exists, wait for it to load
      const checkLoaded = () => {
        if (window.google && window.google.maps && window.google.maps.places) {
          resolve();
        } else {
          setTimeout(checkLoaded, 100);
        }
      };
      checkLoaded();
      return;
    }

    // Create unique callback name
    const callbackName = `initGoogleMaps_${Date.now()}`;
    
    // Set up global callback
    window[callbackName] = () => {
      delete window[callbackName];
      resolve();
    };

    // Create and load script
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places&callback=${callbackName}`;
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      delete window[callbackName];
      reject(new Error('Failed to load Google Maps script'));
    };
    
    document.head.appendChild(script);
  });
};

export const extractAddressComponents = (place) => {
  const components = {
    streetNumber: '',
    route: '',
    addressLine1: '',
    city: '',
    state: '',
    zip: '',
    country: ''
  };

  if (!place.address_components) {
    return components;
  }

  for (const component of place.address_components) {
    const types = component.types;

    if (types.includes('street_number')) {
      components.streetNumber = component.long_name;
    } else if (types.includes('route')) {
      components.route = component.long_name;
    } else if (types.includes('locality')) {
      components.city = component.long_name;
    } else if (types.includes('administrative_area_level_1')) {
      components.state = component.short_name;
    } else if (types.includes('postal_code')) {
      components.zip = component.long_name;
    } else if (types.includes('country')) {
      components.country = component.long_name;
    }
  }

  // Combine street number and route for addressLine1
  if (components.streetNumber && components.route) {
    components.addressLine1 = `${components.streetNumber} ${components.route}`;
  } else if (components.route) {
    components.addressLine1 = components.route;
  } else {
    components.addressLine1 = place.formatted_address || '';
  }

  return components;
};

export const createAutocomplete = (inputElement, onPlaceSelected, options = {}) => {
  if (!window.google || !window.google.maps) {
    throw new Error('Google Maps not loaded');
  }

  const defaultOptions = {
    types: ['address'],
    ...options
  };

  const autocomplete = new window.google.maps.places.Autocomplete(
    inputElement,
    defaultOptions
  );

  autocomplete.addListener('place_changed', () => {
    const place = autocomplete.getPlace();
    
    if (place.formatted_address) {
      const addressData = {
        formattedAddress: place.formatted_address,
        components: extractAddressComponents(place),
        placeId: place.place_id,
        geometry: place.geometry
      };
      
      onPlaceSelected(addressData);
    }
  });

  return autocomplete;
};

export const cleanupAutocomplete = (autocomplete) => {
  if (autocomplete && window.google && window.google.maps) {
    try {
      window.google.maps.event.clearInstanceListeners(autocomplete);
    } catch (error) {
      console.warn('Error cleaning up autocomplete:', error);
    }
  }
};

export const formatAddressForDisplay = (addressData) => {
  if (typeof addressData === 'string') {
    return addressData;
  }
  
  if (addressData && addressData.formattedAddress) {
    return addressData.formattedAddress;
  }
  
  return '';
};

export const constructFullAddress = (addressComponents) => {
  if (!addressComponents) return '';

  const parts = [];

  if (addressComponents.addressLine1) parts.push(addressComponents.addressLine1);
  if (addressComponents.addressLine2) parts.push(addressComponents.addressLine2);
  if (addressComponents.city) parts.push(addressComponents.city);
  if (addressComponents.state) parts.push(addressComponents.state);
  if (addressComponents.zip) parts.push(addressComponents.zip);

  return parts.join(', ');
};
